import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Options, preventOverflow } from '@popperjs/core';

export interface InfoHelpItem {
  icon: string; // Bootstrap Icons class, e.g. "bi-person-badge"
  title: string;
  text: string;
}

/**
 * ng-bootstrap replaces Popper's "stay inside the screen" modifier with an empty function, so a card opened near the
 * right edge of a phone is cut off instead of sliding back into view. This puts the real modifier back, keeping the
 * card 12px from the screen edge. Use it as `[popperOptions]` on any `ngbPopover` that opens a wide card.
 */
export function keepCardInViewport(options: Partial<Options>): Partial<Options> {
  return {
    ...options,
    modifiers: [
      ...(options.modifiers ?? []).filter((m) => m.name !== 'preventOverflow'),
      { ...preventOverflow, enabled: true, options: { padding: 12, altAxis: false } },
    ],
  };
}

/**
 * A small (i) icon that shows a help card on hover or focus: a list of short steps, each with an icon.
 * Meant to sit right after a page title: `<app-page-header ...><app-info-help headerSuffix [items]="..."></app-info-help></app-page-header>`.
 */
@Component({
  selector: 'app-info-help',
  templateUrl: './info-help.component.html',
  styleUrls: ['./info-help.component.css']
})
export class InfoHelpComponent implements OnDestroy {
  @Input() title: string = 'How to use this page';
  @Input() items: InfoHelpItem[] = [];

  popperOptions = keepCardInViewport;

  @ViewChild(NgbPopover) private popover?: NgbPopover;
  private closeTimer?: ReturnType<typeof setTimeout>;

  show() {
    clearTimeout(this.closeTimer);
    if (this.popover && !this.popover.isOpen()) this.popover.open();
  }

  // Closes a moment after the mouse leaves the icon, unless it has moved onto the card (so a tall card can be scrolled).
  hideSoon() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.popover?.close(), 200);
  }

  onShown() {
    const cards = document.querySelectorAll('.ih-popover');
    const card = cards[cards.length - 1];
    card?.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
    card?.addEventListener('mouseleave', () => this.hideSoon());
  }

  ngOnDestroy() {
    clearTimeout(this.closeTimer);
    this.popover?.close();
  }
}
